import React, { useEffect, useState } from "react";
import axios from "axios";
import { Mail, Phone, MapPin, Send, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import HoursWidget from "@/components/HoursWidget";
import BookingForm from "@/components/BookingForm";
import { useSettings } from "@/context/SettingsContext";
import { useLocation } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ContactForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "General Inquiry",
    message: "",
  });
  const [status, setStatus] = useState({ loading: false, success: false, error: "" });

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, success: false, error: "" });
    try {
      await axios.post(`${API}/contact`, form);
      setStatus({ loading: false, success: true, error: "" });
      setForm({ name: "", email: "", phone: "", subject: "General Inquiry", message: "" });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      setStatus({ loading: false, success: false, error: String(msg) });
    }
  };

  const inputCls =
    "w-full rounded-xl border-2 border-stone-200 bg-stone-50 px-4 py-3 text-base text-leaf-900 placeholder:text-stone-400 focus:border-leaf-500 focus:bg-white focus:outline-none focus:ring-4 focus:ring-leaf-200 transition";

  return (
    <form
      data-testid="contact-form"
      onSubmit={onSubmit}
      className="space-y-4"
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="cf-name" className="mb-1.5 block text-sm font-semibold text-leaf-900">
            Your Name *
          </label>
          <input
            id="cf-name"
            data-testid="contact-name"
            required
            name="name"
            value={form.name}
            onChange={onChange}
            className={inputCls}
            placeholder="Alex Rider"
          />
        </div>
        <div>
          <label htmlFor="cf-email" className="mb-1.5 block text-sm font-semibold text-leaf-900">
            Email *
          </label>
          <input
            id="cf-email"
            data-testid="contact-email"
            required
            type="email"
            name="email"
            value={form.email}
            onChange={onChange}
            className={inputCls}
            placeholder="you@email.com"
          />
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="cf-phone" className="mb-1.5 block text-sm font-semibold text-leaf-900">
            Phone
          </label>
          <input
            id="cf-phone"
            data-testid="contact-phone"
            name="phone"
            value={form.phone}
            onChange={onChange}
            className={inputCls}
            placeholder="(XXX) XXX-XXXX"
          />
        </div>
        <div>
          <label htmlFor="cf-subject" className="mb-1.5 block text-sm font-semibold text-leaf-900">
            Subject
          </label>
          <select
            id="cf-subject"
            data-testid="contact-subject"
            name="subject"
            value={form.subject}
            onChange={onChange}
            className={inputCls}
          >
            <option>General Inquiry</option>
            <option>Repairs Question</option>
            <option>Bike Fitting</option>
            <option>Custom Build</option>
            <option>Classes</option>
            <option>Youth Programs</option>
          </select>
        </div>
      </div>
      <div>
        <label htmlFor="cf-msg" className="mb-1.5 block text-sm font-semibold text-leaf-900">
          Message *
        </label>
        <textarea
          id="cf-msg"
          data-testid="contact-message"
          required
          rows={5}
          name="message"
          value={form.message}
          onChange={onChange}
          className={inputCls}
          placeholder="How can we help?"
        />
      </div>

      {status.error && (
        <div
          data-testid="contact-error"
          className="flex items-start gap-2 rounded-xl bg-clay-500/10 px-4 py-3 text-clay-600"
        >
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
          <span className="text-sm">{status.error}</span>
        </div>
      )}

      {status.success && (
        <div
          data-testid="contact-success"
          className="flex items-start gap-2 rounded-xl bg-leaf-100 px-4 py-3 text-leaf-800"
        >
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
          <span className="text-sm font-semibold">
            Message sent! We'll get back to you soon.
          </span>
        </div>
      )}

      <button
        type="submit"
        data-testid="contact-submit"
        disabled={status.loading}
        className="flex w-full items-center justify-center gap-2 rounded-full bg-leaf-600 px-6 py-4 text-base font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-leaf-700 hover:shadow-lg disabled:opacity-60 sm:w-auto sm:px-10"
      >
        {status.loading ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" /> Sending…
          </>
        ) : (
          <>
            Send Message <Send className="h-4 w-4" />
          </>
        )}
      </button>
    </form>
  );
}

export default function Contact() {
  const location = useLocation();
  const { settings } = useSettings();

  useEffect(() => {
    if (location.hash === "#booking") {
      setTimeout(() => {
        const el = document.getElementById("booking");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 150);
    }
  }, [location]);

  return (
    <div data-testid="page-contact">
      {/* Header */}
      <section className="px-4 pt-12 pb-8 sm:px-8 md:px-12 md:pt-16">
        <div className="mx-auto max-w-6xl">
          <p className="text-sm font-black uppercase tracking-[0.22em] text-leaf-700">
            Say hi
          </p>
          <h1 className="mt-3 font-display text-5xl font-black leading-[0.95] tracking-tight text-leaf-900 sm:text-6xl md:text-7xl">
            Let's <span className="marker-underline italic text-leaf-700">talk bikes.</span>
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-leaf-900/80">
            Ping us with a question, book a service, or stop by during open
            hours. We read every message — no auto-replies here.
          </p>
        </div>
      </section>

      {/* Contact cards */}
      <section className="px-4 py-6 sm:px-8 md:px-12">
        <div className="mx-auto grid max-w-6xl gap-4 sm:grid-cols-3">
          <a
            href={`mailto:${settings.email}`}
            data-testid="contact-card-email"
            className="group rounded-3xl border-2 border-leaf-100 bg-white p-6 transition-all hover:-translate-y-1 hover:border-leaf-300 hover:shadow-lg"
          >
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-leaf-100 text-leaf-700">
              <Mail className="h-5 w-5" />
            </span>
            <p className="mt-3 text-xs font-black uppercase tracking-widest text-leaf-700">
              Email
            </p>
            <p className="mt-1 break-all font-semibold text-leaf-900 group-hover:underline">
              {settings.email}
            </p>
          </a>
          <a
            href={`tel:${(settings.phone || "").replace(/\D/g, "")}`}
            data-testid="contact-card-phone"
            className="group rounded-3xl border-2 border-leaf-100 bg-white p-6 transition-all hover:-translate-y-1 hover:border-leaf-300 hover:shadow-lg"
          >
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-sun-100 text-sun-600">
              <Phone className="h-5 w-5" />
            </span>
            <p className="mt-3 text-xs font-black uppercase tracking-widest text-leaf-700">
              Phone
            </p>
            <p className="mt-1 font-semibold text-leaf-900 group-hover:underline">
              {settings.phone}
            </p>
          </a>
          <div
            data-testid="contact-card-address"
            className="rounded-3xl border-2 border-leaf-100 bg-white p-6"
          >
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-leaf-100 text-leaf-600">
              <MapPin className="h-5 w-5" />
            </span>
            <p className="mt-3 text-xs font-black uppercase tracking-widest text-leaf-700">
              Visit
            </p>
            <p className="mt-1 font-semibold text-leaf-900">
              {settings.address}
            </p>
          </div>
        </div>
      </section>

      {/* Form + Hours */}
      <section className="px-4 py-12 sm:px-8 md:px-12">
        <div className="mx-auto grid max-w-6xl gap-8 md:grid-cols-[1.4fr_1fr]">
          <div className="rounded-[2rem] border-2 border-leaf-200 bg-white p-6 sm:p-10">
            <h2 className="font-display text-3xl font-bold text-leaf-900 sm:text-4xl">
              Send us a note
            </h2>
            <p className="mt-2 text-leaf-900/70">
              Questions, feedback, partnerships — all welcome.
            </p>
            <div className="mt-6">
              <ContactForm />
            </div>
          </div>
          <HoursWidget />
        </div>
      </section>

      {/* Map */}
      <section className="px-4 py-12 sm:px-8 md:px-12">
        <div className="mx-auto max-w-6xl">
          <h2 className="font-display text-3xl font-bold text-leaf-900 sm:text-4xl">
            Find us
          </h2>
          <p className="mt-2 text-leaf-900/70">
            {settings.address}
          </p>
          <div
            data-testid="contact-map"
            className="mt-6 overflow-hidden rounded-[2rem] border-4 border-white shadow-[0_20px_60px_rgba(21,128,61,0.15)]"
          >
            <iframe
              title="Last Stop Bike Shop location map"
              src={settings.maps_embed}
              width="100%"
              height="420"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
        </div>
      </section>

      {/* Booking */}
      <section id="booking" className="scroll-mt-24 bg-leaf-50 px-4 py-16 sm:px-8 md:px-12">
        <div className="mx-auto max-w-4xl">
          <p className="text-sm font-black uppercase tracking-[0.22em] text-leaf-600">
            Request service
          </p>
          <h2 className="mt-2 font-display text-4xl font-bold text-leaf-900 sm:text-5xl">
            Reserve your wrench time.
          </h2>
          <p className="mt-3 max-w-2xl text-leaf-900/75">
            Tell us a little about your bike and what you need. We'll confirm
            your appointment within one business day.
          </p>
          <div className="mt-8 rounded-[2rem] border-2 border-leaf-200 bg-white p-6 sm:p-10">
            <BookingForm />
          </div>
        </div>
      </section>
    </div>
  );
}
